import cn from 'classnames';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useBoolean } from '@sravni/react-utils';

import composeRefs from '@seznam/compose-react-refs';

import type { ISpecialOffersContext } from '../../../../contexts/OffersPageQueryContext';
import { SpecialOffersContext } from '../../../../contexts/OffersPageQueryContext';
import { getKeyForItem } from '../../../../helpers/getKeyForItem';
import type { EventPayload } from '../../../../helpers/sendMessageToParentWindow';
import type { EnrichedOffer } from '../../../../interfaces/offers';
import type { TActionTypes } from '../../../../types/actions';
import { Block } from '../../../Block';
import blockStyles from '../../../Block/block.module.scss';

import styles from './horizontal-block-list.module.scss';

interface ICommonBlockList {
  contentRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  blocksInTwoRows?: boolean;
  maxDesktopVisibleItems: number;
  handleEvent: (event: EventPayload) => void;
}

export interface IOffersBlockList extends ICommonBlockList {
  items: EnrichedOffer[];
}

export interface IActionBlockList extends ICommonBlockList {
  items: TActionTypes[];
}

export const HorizontalBlockList: React.FC<IActionBlockList | IOffersBlockList> = ({
  items = [],
  contentRef,
  isMobile,
  maxDesktopVisibleItems,
  blocksInTwoRows,
  ...props
}) => {
  const [isFirstScrolled, setFirstScrolled] = useBoolean(false);
  const [isLastScrolled, setLastScrolled] = useBoolean(false);
  const blockListRef = useRef<HTMLDivElement>(null);
  const firstBlockRef = useRef<HTMLAnchorElement>(null);
  const isMax3BlocksVisible = useMediaQuery({ query: '(min-width: 650px) and (max-width: 980px)' });
  const { isPopup, isVerticalOnMobile } = useContext<ISpecialOffersContext>(SpecialOffersContext);

  useEffect(() => {
    const options = {
      root: blockListRef.current,
      rootMargin: '0px 1px',
      threshold: [1],
    };

    const lastBlock = Array.from(blockListRef.current.getElementsByClassName(blockStyles.block))
      .reverse()
      .find((element) => getComputedStyle(element).display === 'flex');

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === firstBlockRef.current) {
          if (entry.intersectionRatio === 1) {
            setFirstScrolled.off();
          } else {
            setFirstScrolled.on();
          }
        }

        if (entry.target === lastBlock) {
          if (entry.intersectionRatio === 1) {
            setLastScrolled.off();
          } else {
            setLastScrolled.on();
          }
        }
      });
    };

    const firstBlockElement = firstBlockRef.current;

    const observer = new IntersectionObserver(callback, options);
    if (firstBlockRef.current) {
      observer.observe(firstBlockElement);
    }
    if (lastBlock) {
      observer.observe(lastBlock);
    }

    return () => {
      if (firstBlockElement) {
        observer.unobserve(firstBlockElement);
      }
      if (lastBlock) {
        observer.unobserve(lastBlock);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef]);

  const visibleItems = useMemo(() => {
    if (isMax3BlocksVisible) return items.slice(0, maxDesktopVisibleItems < 3 ? maxDesktopVisibleItems : 3);
    if (isPopup) return items.slice(0, 4);

    if (isMobile) {
      return isVerticalOnMobile ? items.slice(0, 3) : items.slice(0, 4);
    }

    if (maxDesktopVisibleItems) return items.slice(0, maxDesktopVisibleItems);

    return items;
  }, [isMax3BlocksVisible, isMobile, isPopup, isVerticalOnMobile, items, maxDesktopVisibleItems]);

  return (
    <div
      className={cn(styles.wrapper, {
        [styles['wrapper--first-scrolled']]: isFirstScrolled,
        [styles['wrapper--last-scrolled']]: isLastScrolled,
      })}
    >
      <div
        className={cn(styles.content, {
          [styles['content--two-rows']]: blocksInTwoRows && !isPopup,
          // [styles['content--mobile-horizontal']]: isPopup && isMobile,
          [styles['content--mobile-horizontal']]: isMobile && !isVerticalOnMobile,
        })}
        ref={composeRefs(contentRef, blockListRef)}
      >
        {visibleItems.map((item, i) => {
          const key = getKeyForItem(item, i);

          const propsForBlock = {
            item,
            key,
            firstBlockRef: i === 0 ? firstBlockRef : null,
            isMobile,
            isPopup,
            ...props,
          };

          return <Block {...propsForBlock} key={key} />;
        })}
      </div>
    </div>
  );
};
